import { sendAjaxRequest } from "./API";
import { useLogger } from "./logger";
import { getCurrentLocalhostPort } from "./util/PortUtils";
const PATH = "content";
const LOGPREFIX = "ContentAPI: ";
const logger = useLogger.getState();
export function getUploadURL(_callback, userId, contentId, partitionId, fileType, bearer, sequence, 
//  getKeys?: boolean,
cacheBusterStore) {
    logger.log(LOGPREFIX +
        "getUploadURL - userId = " +
        userId +
        ", contentId = " +
        contentId +
        ", partitionId = " +
        partitionId +
        ", fileType = " +
        fileType);
    const payload = {
        operation: "getUploadURL",
        contentId,
        fileType,
        partition: partitionId,
        userId: userId,
        sequence,
    };
    // Delegate to sendAjaxRequest so this authenticated POST goes through the
    // global serialization queue and gets its bearer + payload.sequence
    // late-bound from the auth store at send time. Without this, the (bearer,
    // sequence) snapshot captured here would race against every other
    // authenticated POST in flight.
    const inner = (ok, status, message) => {
        let response;
        if (message) {
            try {
                response = JSON.parse(message);
            }
            catch (err) {
                logger.error(LOGPREFIX + "getUploadURL parse error: " + err);
            }
        }
        _callback(ok, status, response, cacheBusterStore);
    };
    sendAjaxRequest(inner, PATH, payload, bearer, cacheBusterStore, logger);
}
export const getContentURL = () => {
    const BASE_URL = "https://www.mediashare.link";
    if (window.location.href.includes("localhost:8080") ||
        window.location.href.includes("test.mediashare.link")) {
        return "https://test.mediashare.link";
    }
    else {
        return BASE_URL;
    }
};
export const getChannelViewBaseURL = () => {
    const localhostPort = getCurrentLocalhostPort();
    if (localhostPort) {
        return `http://localhost:${localhostPort}/ChannelView?/@`;
    }
    else if (window.location.href.includes("test.mediashare.link")) {
        return "https://test.mediashare.link/@";
    }
    else {
        return "https://www.mediashare.link/@";
    }
};
export const getSiteBaseURL = () => {
    const localhostPort = getCurrentLocalhostPort();
    if (localhostPort) {
        return `http://localhost:${localhostPort}/`;
    }
    else if (window.location.href.includes("test.mediashare.link")) {
        return "https://test.mediashare.link/";
    }
    else {
        return "https://www.mediashare.link/";
    }
};
export const isProd = () => {
    var _a, _b, _c, _d;
    if (((_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.href) === null || _b === void 0 ? void 0 : _b.includes("mediashare.link")) &&
        !((_d = (_c = window === null || window === void 0 ? void 0 : window.location) === null || _c === void 0 ? void 0 : _c.href) === null || _d === void 0 ? void 0 : _d.includes("test.mediashare.link"))) {
        return true;
    }
    else {
        return false;
    }
};
/**
 * Generate URL for studio content (completed job results)
 * @param userId - Job owner user ID
 * @param jobId - Job ID
 * @param fileName - File name (e.g., 'generated_01.jpg')
 * @param bearer - Bearer token for authentication
 * @returns Full URL to studio content with authentication
 */
export const getStudioContentURL = (userId, jobId, fileName, bearer) => {
    const baseURL = getContentURL();
    const path = `u/${userId}/studio/${jobId}/${fileName}`;
    if (bearer) {
        return `${baseURL}/${path}?bearer=${encodeURIComponent(bearer)}`;
    }
    else {
        return `${baseURL}/${path}`;
    }
};
/**
 * Generate URL for temporary studio files (during job processing)
 * @param userId - User ID
 * @param jobId - Job ID
 * @param fileName - File name
 * @param bearer - Bearer token for authentication
 * @returns Full URL to temporary content with authentication
 */
export const getStudioTempURL = (userId, jobId, fileName, bearer) => {
    const baseURL = getContentURL();
    const path = `tmp/${userId}/studio/${jobId}/${fileName}`;
    if (bearer) {
        return `${baseURL}/${path}?bearer=${encodeURIComponent(bearer)}`;
    }
    else {
        return `${baseURL}/${path}`;
    }
};
/**
 * Generate URL for studio job metadata
 * @param userId - Job owner user ID
 * @param jobId - Job ID
 * @param bearer - Bearer token for authentication
 * @returns Full URL to job metadata JSON
 */
export const getStudioJobMetadataURL = (userId, jobId, bearer) => {
    return getStudioContentURL(userId, jobId, "jobDetails.json", bearer);
};
/**
 * Generate authenticated URL for any studio-related content
 * @param path - Relative path (e.g., 's/userId/jobId/file.jpg' or 'tmp/userId/jobId/file.jpg')
 * @param bearer - Bearer token for authentication
 * @returns Full authenticated URL
 */
export const getAuthenticatedStudioURL = (path, bearer) => {
    const baseURL = getContentURL();
    // Remove leading slash if present
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    if (bearer) {
        return `${baseURL}/${cleanPath}?bearer=${encodeURIComponent(bearer)}`;
    }
    else {
        return `${baseURL}/${cleanPath}`;
    }
};
export function getChannelId(_callback, channelName, cacheBusterStore) {
    logger.log(LOGPREFIX + "getChannelId - channelName = " + channelName);
    const payload = {
        operation: "getChannelId",
        channelName,
    };
    // Unauthenticated lookup — sendAjaxRequest with bearer=undefined bypasses
    // the authenticated queue. Routed through it for consistency.
    const inner = (ok, status, message) => {
        let response;
        if (message) {
            try {
                response = JSON.parse(message);
            }
            catch (err) {
                logger.error(LOGPREFIX + "getChannelId parse error: " + err);
            }
        }
        _callback(ok, status, response, cacheBusterStore);
    };
    sendAjaxRequest(inner, "channelId", payload, undefined, cacheBusterStore, logger);
}
export function updateTypes(_callback, userId, contentId, partitionId, bearer, sequence, cacheBusterStore) {
    logger.log(LOGPREFIX +
        "updateTypes - userId = " +
        userId +
        ", contentId = " +
        contentId +
        ", partitionId = " +
        partitionId);
    const payload = {
        operation: "updateTypes",
        contentId,
        partition: partitionId,
        userId: userId,
        fileType: undefined,
        sequence,
    };
    // Delegate to sendAjaxRequest for queue + late-binding (see getUploadURL).
    const inner = (ok, status, message) => {
        let response;
        if (message) {
            try {
                response = JSON.parse(message);
            }
            catch (err) {
                logger.error(LOGPREFIX + "updateTypes parse error: " + err);
            }
        }
        _callback(ok, status, response, cacheBusterStore);
    };
    sendAjaxRequest(inner, PATH, payload, bearer, cacheBusterStore, logger);
}
